import { ProgressBar } from '../../components/ProgressBar/ProgressBar';
import { ItemRow } from './ItemRow';
import type { Category } from '@budgetx/shared';
import { kr, signKr } from '../../lib/format';
import styles from './CategoryRow.module.css';

interface Props {
  category: Category;
  month: number;
  isOpen: boolean;
  onToggle(): void;
  onRenameCategory(name: string): void;
  onRenameItem(itemId: number, name: string): void;
  onBudgetChange(itemId: number, next: number): void;
  onActualChange(itemId: number, next: number | null): void;
  onDeleteItem(itemId: number): void;
  onDeleteCategory(): void;
  onAddItem(): void;
}

export function CategoryRow({
  category,
  month,
  isOpen,
  onToggle,
  onRenameCategory,
  onRenameItem,
  onBudgetChange,
  onActualChange,
  onDeleteItem,
  onDeleteCategory,
  onAddItem,
}: Props) {
  const isIncome = category.kind === 'income';
  const isSavings = category.kind === 'opsparing';

  const totalBudget = category.items.reduce((s, it) => s + (it.budgets[month] ?? 0), 0);
  const totalActual = category.items.reduce((s, it) => s + (it.actuals[month] ?? 0), 0);
  const diff = isIncome ? totalActual - totalBudget : totalBudget - totalActual;
  const over = totalActual > totalBudget;
  const pct = totalBudget > 0 ? Math.min(100, Math.round((totalActual / totalBudget) * 100)) : 0;
  const color = isSavings ? 'var(--accent)' : over ? 'var(--danger)' : 'var(--ok)';

  const diffText = isSavings ? '±0 kr.' : signKr(diff);
  const diffClass = isSavings ? styles.diffNeutral : diff < 0 ? styles.diffOver : styles.diffOk;

  return (
    <div className={styles.wrap}>
      <div className={styles.headerWrap}>
        <div className={styles.header}>
          <div className={styles.nameCell}>
            <div className={styles.nameLine}>
              <input
                className={styles.nameInput}
                value={category.name}
                onChange={(e) => onRenameCategory(e.target.value)}
                onFocus={(e) => e.target.select()}
              />
              {category.source_label && (
                <span className={styles.source}>{category.source_label}</span>
              )}
            </div>
            <div className={styles.progress}>
              <ProgressBar percent={pct} color={color} />
            </div>
          </div>
          <button className={styles.toggleBtn} onClick={onToggle}>
            <div className={`${styles.num} ${styles.numMuted}`}>{kr(totalBudget)}</div>
            <div className={`${styles.num} ${styles.numBold}`}>{kr(totalActual)}</div>
            <div className={`${styles.num} ${styles.numBold} ${diffClass}`}>{diffText}</div>
            <div className={styles.chevron}>{isOpen ? '–' : '+'}</div>
          </button>
        </div>
        <button
          className={styles.deleteBtn}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteCategory();
          }}
          title={`Slet kategorien ${category.name}`}
          aria-label={`Slet kategorien ${category.name}`}
        >
          ×
        </button>
      </div>

      {isOpen && (
        <div className={styles.body}>
          {category.items.map((it) => (
            <ItemRow
              key={it.id}
              item={it}
              month={month}
              isSavings={isSavings}
              isIncome={isIncome}
              onRename={(n) => onRenameItem(it.id, n)}
              onBudgetChange={(v) => onBudgetChange(it.id, v)}
              onActualChange={(v) => onActualChange(it.id, v)}
              onDelete={() => onDeleteItem(it.id)}
            />
          ))}
          <div className={styles.addRow}>
            <button className={styles.addBtn} onClick={onAddItem}>
              + Tilføj postering
            </button>
            <span className={styles.addHint}>Tilføjes til alle måneder i året</span>
          </div>
        </div>
      )}
    </div>
  );
}
